import { parseCookies } from '@cian/utils/lib/shared/cookie';

export interface ISendUXSEventWhenReadyOptions {
  /** Интервал опроса готовности виджета, мс. */
  interval?: number;
  /** Максимальное время ожидания готовности, мс. */
  maxWait?: number;
  /**
   * Ключ дедупликации: событие с одним ключом уходит максимум один раз за загрузку страницы.
   * Нужен маунт-событиям (`open_zhk_card`) — useRef-защита в хуке сбрасывается при
   * ремаунте контейнера, дубль засчитывается бэкендом UXFB как лишний «заход», и кампании
   * с правилом «показ на 2-й заход» выезжают уже на первом.
   */
  dedupeKey?: string;
}

const DEFAULT_INTERVAL = 500;
const DEFAULT_MAX_WAIT = 10000;

const sentDedupeKeys = new Set<string>();

/** Сбрасывает модульный дедуп по dedupeKey — только для тестов. */
export const resetUXSEventDedupe = () => {
  sentDedupeKeys.clear();
};

/**
 * cid (аналитический client id) из куки `_CIAN_GK` — общее свойство UXFB-событий с атрибутами
 * (SEL-873): web обогащает события атрибутом `cid`, аппки — `device_id`.
 */
const getCid = (): string | undefined =>
  typeof document === 'undefined' ? undefined : parseCookies(document.cookie)['_CIAN_GK'];

export const sendUXSEvent = (name: string, attributes?: Record<string, string>) => {
  if (typeof window === 'undefined' || !window.UXS) {
    return;
  }

  if (attributes) {
    const cid = getCid();

    window.UXS.sendEvent(name, cid ? { ...attributes, cid } : attributes);
  } else {
    window.UXS.sendEvent(name);
  }
};

/**
 * Отправляет UXFB-событие, дождавшись готовности виджета `window.UXS` (грузится через GTM).
 *
 * Основной канал ожидания — document-событие `uxsReady`, которое SDK диспатчит после инициализации.
 * Поллинг с интервалом `interval` до `maxWait` — страховка на случай, когда `uxsReady` отгремел
 * до подписки. Подписка на `uxsReady` живёт и после `maxWait`, поэтому при поздней загрузке SDK
 * событие всё равно доедет; если виджет так и не появится — молча ничего не отправляем, без throw.
 */
export const sendUXSEventWhenReady = (
  name: string,
  attributes?: Record<string, string>,
  options: ISendUXSEventWhenReadyOptions = {},
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const { interval = DEFAULT_INTERVAL, maxWait = DEFAULT_MAX_WAIT, dedupeKey } = options;

  if (dedupeKey) {
    if (sentDedupeKeys.has(dedupeKey)) {
      return;
    }

    sentDedupeKeys.add(dedupeKey);
  }

  if (window.UXS) {
    sendUXSEvent(name, attributes);

    return;
  }

  let sent = false;

  const handleReady = () => {
    if (sent) {
      return;
    }

    sent = true;
    document.removeEventListener('uxsReady', handleReady);
    sendUXSEvent(name, attributes);
  };

  document.addEventListener('uxsReady', handleReady);

  let waited = 0;
  const timerId = setInterval(() => {
    waited += interval;

    if (window.UXS) {
      clearInterval(timerId);
      handleReady();
    } else if (sent || waited >= maxWait) {
      clearInterval(timerId);
    }
  }, interval);
};
